class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, DeviceUtils.isMobileSmall ? 800 : 450);

    const facetForm = this.querySelector('form');

    facetForm.addEventListener('input', (event) => {
      if (event.target.hasAttribute('data-no-facet')) return;
      const animatedList = event.target.closest('animated-list');
      if (animatedList) animatedList.preventAutoClose = true;
      this.debouncedOnSubmit(event);
    });

    facetForm.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() !== 'ENTER') return;
      const checkbox = event.target.closest('input[type="checkbox"]');
      if (!checkbox) return;
      event.preventDefault();
      checkbox.checked = !checkbox.checked;
      checkbox.dispatchEvent(new Event('input', { bubbles: true }));
    });

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    const currentParams = new URLSearchParams(window.location.search);
    const nextParams = new URLSearchParams(searchParams);
    const nextView = nextParams.get('view') || currentParams.get('view');
    if (nextView) {
      nextParams.set('view', nextView);
    }
    const nextSort = nextParams.get('sort_by');
    if (nextSort) {
      nextParams.set('sort_by', nextSort);
    }
    searchParams = nextParams.toString();
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading-spinner, facet-filters-form .loading-spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      if (Shopify.designMode) {
        FacetFiltersForm.renderSectionFromFetch(url, event);
      } else {
        FacetFiltersForm.filterData.some(filterDataUrl)
          ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
          : FacetFiltersForm.renderSectionFromFetch(url, event);
      }
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html, event);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html, event);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html, event) {
    const productGridContainer = document.getElementById('ProductGridContainer');
    productGridContainer.innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    const scaleRange = document.querySelector('scale-range');
    if (scaleRange && typeof scaleRange.handleApply === 'function') {
      scaleRange.handleApply();
    }
  }

  static renderProductCount(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const countEl = parsed.getElementById('ProductCount');

    if (!countEl) return;

    const count = countEl.innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    if (container) {
      container.innerHTML = count;
      container.classList.remove('loading');
    }
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading-spinner, facet-filters-form .loading-spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);

      if (currentElement) {
        currentElement.innerHTML = elementToRender.innerHTML;
        return;
      }

      if (index > 0) {
        const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
        if (elementToRender.className === previousElementClassName) {
          const prev = document.getElementById(previousElementId);
          if (prev) prev.after(elementToRender);
          return;
        }
      }

      if (elementToRender.parentElement) {
        const target = document.querySelector(
          `#${elementToRender.parentElement.id} .js-filter`
        );
        if (target) target.before(elementToRender);
      }
    });


    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilter = event.target.closest('.js-filter');
      const closestJSFilterID = closestJSFilter.id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));
      }

      const animatedList = closestJSFilter.querySelector('animated-list');
      if (animatedList) animatedList.preventAutoClose = false;
    }

    if (window.tooltipInstance) {
      window.tooltipInstance.hideTooltip();
      window.tooltipInstance.init(document.getElementById('main-collection-filters'));
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      const htmlElement = html.querySelector(selector);
      if (!htmlElement) return;

      const target = document.querySelector(selector);
      if (!target) return;

      target.innerHTML = htmlElement.innerHTML;
    });
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.details__summary');
    const sourceSummary = source.querySelector('.details__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    if (event.target.hasAttribute('data-no-facet')) return;
    event.preventDefault();

    const drawer = document.querySelector('filter-drawer');
    if (drawer) drawer.hasFilterChanges = true;

    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.target.classList.contains('mobile-facets__checkbox')) {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form')?.id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
if (!customElements.get('facet-filters-form')) {
  customElements.define('facet-filters-form', FacetFiltersForm);
}
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);

class PriceSlider extends HTMLElement {
  constructor() {
    super();

    [this.minInput, this.maxInput] = this.querySelectorAll('.price-slider__input--range');
    this.inverseLeft = this.querySelector('.price-slider__track--left');
    this.priceRange = this.querySelector('.price-slider__range');
    [this.priceThumbMin, this.priceThumbMax] = this.querySelectorAll('.price-slider__thumb');
    [this.minInputCustom, this.maxInputCustom] = this.querySelectorAll('.price-slider__input--number');
    [this.minInputTooltip, this.maxInputTooltip] = this.querySelectorAll('.price-slider__tooltip');

    this.minInput.setAttribute('value', this.minInput.value);
    this.maxInput.setAttribute('value', this.maxInput.value);

    this.handleMinInput = () => this.minEvent();
    this.handleMaxInput = () => this.maxEvent();
    this.handleMouseEnterMin = () => !this.closest('.disable-filters') && this.priceThumbMin.classList.add('price-slider__thumb--hovered');
    this.handleMouseLeaveMin = () => this.priceThumbMin.classList.remove('price-slider__thumb--hovered');
    this.handleMouseEnterMax = () => !this.closest('.disable-filters') && this.priceThumbMax.classList.add('price-slider__thumb--hovered');
    this.handleMouseLeaveMax = () => this.priceThumbMax.classList.remove('price-slider__thumb--hovered');

    this.resizeTimeout = null;
    this.handleResize = this.handleResize.bind(this);
  }

  connectedCallback() {
    if (!this.minInput || !this.maxInput) return;

    this.minInput.addEventListener('input', this.handleMinInput);
    this.maxInput.addEventListener('input', this.handleMaxInput);

    this.minInput.addEventListener('mouseenter', this.handleMouseEnterMin);
    this.minInput.addEventListener('mouseleave', this.handleMouseLeaveMin);
    this.maxInput.addEventListener('mouseenter', this.handleMouseEnterMax);
    this.maxInput.addEventListener('mouseleave', this.handleMouseLeaveMax);

    this.minEvent();
    this.maxEvent();

    window.addEventListener('resize', this.handleResize);
  }

  handleResize() {
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      this.minEvent();
      this.maxEvent();
    }, 150);
  }

  disconnectedCallback() {
    if (!this.minInput || !this.maxInput) return;
    this.minInput.removeEventListener('input', this.handleMinInput);
    this.maxInput.removeEventListener('input', this.handleMaxInput);
    this.minInput.removeEventListener('mouseenter', this.handleMouseEnterMin);
    this.minInput.removeEventListener('mouseleave', this.handleMouseLeaveMin);
    this.maxInput.removeEventListener('mouseenter', this.handleMouseEnterMax);
    this.maxInput.removeEventListener('mouseleave', this.handleMouseLeaveMax);
    clearTimeout(this.resizeTimeout);
  }

  formatPrice(value) {
    if (typeof formatMoneyShopify === 'function' && this.dataset.moneyFormat && this.dataset.currency) {
      return formatMoneyShopify(value, this.dataset.moneyFormat, this.dataset.currency);
    }
    return value;
  }

  updateHelpInputs() {
    if (this.minInputCustom) this.minInputCustom.value = (this.minInput.value / 100).toFixed(2);
    if (this.maxInputCustom) this.maxInputCustom.value = (this.maxInput.value / 100).toFixed(2);
  }

  resetCustomInputs() {
    const minVal = Number(this.minInput.value);
    const maxVal = Number(this.maxInput.value);
    const maxAttr = Number(this.maxInput.max);

    if (minVal === 0 && maxVal === maxAttr) {
      if (this.minInputCustom) this.minInputCustom.value = '';
      if (this.maxInputCustom) this.maxInputCustom.value = '';
      return true;
    }
    return false;
  }

  minEvent(val) {
    val = val ?? this.minInput.value;
    const maxAttr = Number(this.maxInput.getAttribute('value'));
    const minVal = Math.min(val, maxAttr - 1);

    this.minInput.value = minVal;
    this.minInput.setAttribute('value', minVal);

    if (!this.resetCustomInputs()) {
      if (this.minInputCustom) this.minInputCustom.value = minVal;
      this.updateHelpInputs();
    }

    const percent = Math.round((this.minInput.value / this.minInput.max) * 100);
    this.inverseLeft.style.width = percent + '%';
    this.priceRange.style.left = percent + '%';
    this.priceThumbMin.style.left = percent + '%';

    if (this.minInputTooltip) {
      const displayMin = minVal === 0
        ? formatMoneyShopify(0, this.dataset.moneyFormat.replace(/{{\s*amount\s*}}/, '{{ amount_no_decimals }}'), this.dataset.currency)
        : this.formatPrice(minVal);
      this.minInputTooltip.innerHTML = displayMin;
      this.checkTooltipOverflow(this.minInputTooltip, percent, 'left');
    }
  }

  maxEvent(val) {
    val = val ?? this.maxInput.value;
    const minAttr = Number(this.minInput.getAttribute('value'));
    const maxVal = Math.max(val, minAttr + 1);

    this.maxInput.value = maxVal;
    this.maxInput.setAttribute('value', maxVal);

    if (!this.resetCustomInputs()) {
      if (this.maxInputCustom) this.maxInputCustom.value = maxVal;
      this.updateHelpInputs();
    }

    const percent = Math.round((this.maxInput.value / this.maxInput.max) * 100);
    this.inverseLeft.style.width = (100 - percent) + '%';
    this.priceRange.style.right = (100 - percent) + '%';
    this.priceThumbMax.style.left = percent + '%';

    if (this.maxInputTooltip) {
      const displayMax = maxVal === 0
        ? formatMoneyShopify(0, this.dataset.moneyFormat.replace(/{{\s*amount\s*}}/, '{{ amount_no_decimals }}'), this.dataset.currency)
        : this.formatPrice(maxVal);
      this.maxInputTooltip.innerHTML = displayMax;
      this.checkTooltipOverflow(this.maxInputTooltip, percent, 'right');
    }
  }

  checkTooltipOverflow(tooltip, percent, side) {
    if (!tooltip) return;
    if (side === 'left') {
      if (percent <= 0) {
        tooltip.classList.add('price-slider__tooltip--overflow');
      } else {
        tooltip.classList.remove('price-slider__tooltip--overflow');
      }
    } else if (side === 'right') {
      if (percent >= 100) {
        tooltip.classList.add('price-slider__tooltip--overflow');
      } else {
        tooltip.classList.remove('price-slider__tooltip--overflow');
      }
    }
  }
}

customElements.define('price-slider', PriceSlider);

class ScaleRange extends HTMLElement {
  constructor() {
    super();
    this.range = this.querySelector('input[type="range"]');
    this.observer = null;
    this.handleApply = null;
    this.updateHeightTimeout = null;
  }

  connectedCallback() {
    if (!this.range) return;

    const prefix = this.dataset.gridPrefix || 'grid--cols-desktop-';
    this.idleHandle = null;

    this.handleApply = () => {
      const grid = document.getElementById('product-grid');
      if (!grid) return;

      grid.className = grid.className
        .split(' ')
        .filter(c => !c.startsWith(prefix))
        .join(' ');

      grid.classList.add(`${prefix}${this.range.value}`);

      const cards = grid.querySelectorAll('product-card');

      this.scheduleCardUpdate(grid, cards);
    };

    this.handleApply();
    this.range.addEventListener('input', this.handleApply);

    this.range.addEventListener('keydown', (e) => {
      const step = Number(this.range.step) || 1;
      const min = Number(this.range.min);
      const max = Number(this.range.max);
      let current = Number(this.range.value);
      let next = null;

      if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
        next = Math.max(min, current - step);
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
        next = Math.min(max, current + step);
      }

      if (next !== null && next !== current) {
        e.preventDefault();
        this.range.value = next;
        this.range.dispatchEvent(new Event('input'));
      }
    });
  }

  scheduleCardUpdate(grid, cards) {
    grid.classList.add('off-product-height');

    if (this.idleHandle) {
      if ('cancelIdleCallback' in window) {
        cancelIdleCallback(this.idleHandle);
      } else {
        clearTimeout(this.idleHandle);
      }
    }

    const run = () => {
      const batch = 10;
      let i = 0;

      const loop = () => {
        const end = Math.min(i + batch, cards.length);

        for (; i < end; i++) {
          const card = cards[i];
          card.updateMoreOptionsCount();
          card.handleResize();
        }

        if (i < cards.length) {
          requestAnimationFrame(loop);
        } else {
          grid.classList.remove('off-product-height');
        }
      };

      loop();
    };

    this.idleHandle = ('requestIdleCallback' in window)
      ? requestIdleCallback(run, { timeout: 500 })
      : setTimeout(run, 0);
  }

  disconnectedCallback() {
    if (this.range && this.handleApply) {
      this.range.removeEventListener('input', this.handleApply);
    }

    if (this.idleHandle) {
      if ('cancelIdleCallback' in window) {
        cancelIdleCallback(this.idleHandle);
      } else {
        clearTimeout(this.idleHandle);
      }
      this.idleHandle = null;
    }
  }
}

customElements.define('scale-range', ScaleRange);

class ScrollableFacet extends HTMLElement {
  constructor() {
    super();
    this.visibleCount = parseInt(this.dataset.visibleRows) || 10;
    this.content = this.querySelector('.scrollable-facet');
    this.observer = null;

    this.handleResize = () => this.updateHeight();
  }

  connectedCallback() {
    if (!this.content) return;
    if (DeviceUtils.isMobileSmall) return;

    const animatedList = this.closest('animated-list');
    const accordion = this.closest('.accordion');

    if (animatedList && !animatedList.isOpen) {
      const panel = animatedList.elements.panel;
      panel.removeAttribute('hidden');
      panel.style.visibility = 'hidden';
      this.updateHeight();
      requestAnimationFrame(() => {
        panel.style.visibility = '';
        panel.setAttribute('hidden', true);
      });
    } else if (accordion) {
      const filter = accordion.querySelector('.js-filter');
      if (filter) {
        filter.style.display = 'block';
        filter.style.visibility = 'hidden';
        this.updateHeight();
        requestAnimationFrame(() => {
          filter.style.display = '';
          filter.style.visibility = '';
        });
      }
    } else {
      this.updateHeight();
    }

    this.observer = new MutationObserver(() => this.updateHeight());
    this.observer.observe(this.content, { childList: true, subtree: true });

    window.addEventListener('resize', this.handleResize);
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    window.removeEventListener('resize', this.handleResize);
  }

  updateHeight() {
    if (DeviceUtils.isMobileSmall) return;
    const items = Array.from(this.content.querySelectorAll('li'));
    if (items.length <= this.visibleCount) {
      this.content.style.maxHeight = 'none';
      this.content.style.overflowY = 'visible';
      return;
    }

    const visibleItems = items.slice(0, this.visibleCount);
    let height = visibleItems.reduce((sum, el) => sum + el.offsetHeight, 0);

    const parent = items[0]?.parentElement;
    if (parent) {
      const styles = window.getComputedStyle(parent);
      const rowGap = parseFloat(styles.rowGap) || 0;
      height += rowGap * (visibleItems.length - 1);
    }

    this.content.style.maxHeight = height + 'px';
    this.content.style.overflowY = 'auto';
  }
}

customElements.define('scrollable-facet', ScrollableFacet);

class FilterDrawer extends withAnimation(HTMLElement) {
  constructor() {
    super();

    this.header = document.querySelector('.section-header');
    this.mainContent = document.querySelector('#MainContent');
    this.mainDetailsToggle = this.querySelector('details');
    this.menuDrawer = this.querySelector('.menu-drawer');
    this.backDrop = this.querySelector('.menu-drawer__backdrop');
    this.scrollContainer = this.querySelector('#FilterDrawer-Scroll');
    this.scrollTrack = this.querySelector('custom-scrollbar');

    this.isOpen = false;
    this.scrollLockEnable = true;

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();

    this.initAnimation({
      duration: 0.3
    });

    DrawerManager.register(this);

    this.handleWindowResize = this.handleWindowResize.bind(this);
    window.addEventListener('resize', this.handleWindowResize);

    const accordions = this.querySelectorAll('.details__checkbox');

    accordions.forEach(accordion => {
      accordion.addEventListener('change', this.handleAccordionChange.bind(this));
    });

    setTimeout(() => {
      const target = this.querySelector('.mobile-facets__open-wrapper');
      target.classList.remove('is-hidden');
      this.mainContent && this.observeFooter();
    }, 1000);

  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.handleWindowResize);
    this.footerObserver?.disconnect();
  }

  observeFooter() {
    const target = this.querySelector('.mobile-facets__open-wrapper');
    if (!target) return;

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        target.classList.remove('is-hidden');
      } else {
        target.classList.add('is-hidden');
      }
    }, {
      rootMargin: `-${screen.availHeight}px 0px ${screen.availHeight}px 0px`
    });

    observer.observe(this.mainContent);
  }

  handleWindowResize() {
    if (!window.DeviceUtils.isMobile && this.isOpen) {
      this.forceClose();
    }
  }

  bindEvents(element = this) {
    element.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    element.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));

    if (element === this && this.backDrop) {
      this.backDrop.addEventListener('click', this.handleBackdropClick.bind(this));
    }
  }

  handleAccordionChange(event) {
    setTimeout(() => {
      if (this.scrollTrack && typeof this.scrollTrack.updateThumbPosition === 'function') {
        this.scrollTrack.updateThumbPosition();
      }
    }, 300);
  }

  handleBackdropClick(event) {
    event.preventDefault();
    this.closeMenuDrawer(event, document.activeElement);
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    if (openDetailsElement === this.mainDetailsToggle) this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'));
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  setupTrapFocus(summaryElement, detailsElement) {
    const addTrapFocus = () => {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    };

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (!reducedMotion || !reducedMotion.matches) {
      summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
    } else {
      addTrapFocus();
    }
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const isOpen = detailsElement.hasAttribute('open');

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);
    } else {
      detailsElement.classList.add('menu-opening');
      summaryElement.setAttribute('aria-expanded', true);

      this.animateStagger(detailsElement, {
        staggerSelector: ':scope > .has-submenu > .menu-drawer__inner-submenu > .menu-drawer__menu > li',
        animationType: 'slide',
        slideDirection: 'right',
        perItemAnimation: true
      });

      this.setupTrapFocus(summaryElement, detailsElement);
    }
  }

  openMenuDrawer(summaryElement) {
    this.isOpen = true;
    DrawerManager.setActiveWithSecondary(this);
    if (DrawerManager.diffrentSide()) {
      DrawerManager.clearSecondaryIfOpen(this);
    }
    DeviceUtils.updateHeaderSizes();
    this.classList.add('menu-open');

    document.body.classList.add('overflow-hidden');
    this.scrollLockEnable && scrollLock.lock();
    requestAnimationFrame(() => {
      this.animateShowContent(summaryElement);
    });
  }

  resetMenuState() {
    this.isOpen = false;
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.classList.remove('menu-opening');

    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });

    this.classList.remove('menu-open');

    this.querySelectorAll('[aria-expanded="true"]').forEach((element) => {
      element.setAttribute('aria-expanded', 'false');
    });

    this.closeOtherPopup();

    DrawerManager.unsetActive(this);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (!elementToFocus) return;
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);
    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  forceClose() {
    this.resetMenuState();
    removeTrapFocus();
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    if (!detailsElement) return;
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    this.animateHideContent(detailsElement);
  }

  onShowActions(summaryElement) {
    this.mainDetailsToggle.classList.add('menu-opening');
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    DrawerManager.clearSecondaryIfOpen(this);
  }

  onCloseActions(detailsElement) {
    detailsElement.removeAttribute('open');
    const parentDetails = detailsElement.closest('details[open]');
    this.closeOtherPopup();
    if (parentDetails) {
      trapFocus(parentDetails, detailsElement.querySelector('summary'));
    }
    this.scrollContainer && (this.scrollContainer.scrollTop = 0);

    if (DeviceUtils.isMobileSmall && this.hasFilterChanges) {
      const productGridContainer = document.getElementById('ProductGridContainer');
      if (productGridContainer) {
        const section = productGridContainer.closest('.shopify-section');
        if (section) {
          section.scrollIntoView({ behavior: 'smooth' });
          requestAnimationFrame(() => {
            DeviceUtils.updateHeaderSizes();
          });
        }
      }
    }

    this.hasFilterChanges = false;
  }

  async animateShowContent(summaryElement) {
    await this.animateSlideIn(this.menuDrawer, {
      staggerSelector: '.menu-drawer__navigation > .menu-drawer__menu > li',
      slideDirection: 'left',
      backDrop: this.backDrop,
      onComplete: () => {
        this.onShowActions(summaryElement);
      }
    });
  }

  async animateHideContent(detailsElement) {
    await this.animateSlideOut(this.menuDrawer, {
      slideDirection: 'right',
      backDrop: this.backDrop,
      onComplete: () => {
        this.resetMenuState();
        this.onCloseActions(detailsElement);
      }
    });
  }

  closeOtherPopup() {
    const megaMenu = document.querySelector('mega-menu');
    if (megaMenu?.classList.contains('mega-menu--block')) {
      megaMenu.classList.remove('mega-menu--block');
      document.body.removeAttribute('data-mega-menu-block');
      megaMenu.forceClose?.();
    }
  }
}

customElements.define('filter-drawer', FilterDrawer);

class DropdownList extends AnimatedList {
  constructor() {
    const options = {
      panelSelector: '.dropdown-list__wrapper',
      toggleButtonSelector: '.dropdown-list__button',
      focusableItemsSelector: '.dropdown-list__item > a',
      containerSelector: 'body',
      trapFocus: true,
      initialOpacity: 0,
      staggerDuration: 0.2,
      stageDelay: 0.03
    };

    super(options);

    this.elements = {
      ...this.elements,
      button: this.querySelector('.dropdown-list__button'),
      items: this.querySelectorAll('.dropdown-list__item > a'),
      text: this.querySelector('.dropdown-list__text'),
      select: document.querySelector(`select[name="${this.dataset.name}"]`)
    };

    this.value = this.dataset.value || null;
    this.initEvents();
    this.syncFromSelect();
  }

  initEvents() {
    this.elements.items.forEach(item => {
      item.addEventListener('click', this.onItemClick.bind(this));
    });

    if (this.elements.select) {
      this.elements.select.addEventListener('change', (e) => {
        this.value = e.target.value;
      });
    }
  }

  onItemClick(event) {
    event.preventDefault();
    if (event.currentTarget.classList.contains('active')) return;

    const form = this.closest('form');
    const { value, name } = event.currentTarget.dataset;
    this.value = value;

    if (this.elements.text) {
      this.elements.text.textContent = name;
    }

    this.elements.items.forEach(item => item.classList.remove('active'));
    event.currentTarget.classList.add('active');
    this.dataset.value = value;
    const fieldName = this.dataset.name;

    if (form) {
      const hiddenInput = form.querySelector(`input[name="${fieldName}"]`);
      if (hiddenInput) {
        hiddenInput.value = value;
        form.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }

    if (this.elements.select) {
      this.elements.select.value = value;
      this.elements.select.dispatchEvent(new Event('change', { bubbles: true }));
    }

    this.close();
  }

  syncFromSelect() {
    if (this.elements.select) {
      const current = this.elements.select.value;
      if (current) {
        this.value = current;
      }
    }
  }

  open() {
    super.open();
    const activeItem = this.querySelector('.dropdown-list__item.active') || this.elements.items[0];
    activeItem?.focus();
    this.classList.add('opened');
  }

  close() {
    super.close();
    this.classList.remove('opened');
  }

  get value() {
    return this.dataset.value;
  }

  set value(val) {
    const item = this.querySelector(`[data-value="${val}"]`);
    if (item) {
      this.elements.items.forEach(i => i.classList.remove('active'));
      item.classList.add('active');
      this.dataset.value = val;
      if (this.elements.text) this.elements.text.textContent = item.dataset.name;
    }
    if (this.elements.select && this.elements.select.value !== val) {
      this.elements.select.value = val;
    }
  }
}

customElements.define('dropdown-list', DropdownList);